import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "../../../component/element/table";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  delete_marketer,
  get_marketers,
} from "../../../services/apis/marketers";
import { toggle_marketer_status } from "../../../services/apis/marketers";
import { IMarketer } from "../../../store/types/marketers";
import ItemAction from "../../../component/ui/action/ItemAction";
import ProfessionalViewModal from "../show/ProfessionalViewModal";
import MarketerFormModal from "./MarketerFormModal";

interface IMarketersTable {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  createOpen: boolean;
  onCreateClose: () => void;
}

const MarketersTable = ({
  page,
  setPage,
  createOpen,
  onCreateClose,
}: IMarketersTable) => {
  const dispatch = useAppDispatch();
  const [viewingMarketerId, setViewingMarketerId] = React.useState<
    number | null
  >(null);
  const [editingMarketer, setEditingMarketer] =
    React.useState<IMarketer | null>(null);
  const { marketers, total, limit, loading, loadingMarketerId } =
    useAppSelector(({ marketer }) => marketer);

  React.useEffect(() => {
    get_marketers(dispatch, (page - 1) * limit, limit);
  }, [dispatch, limit, page]);

  const columns: ColumnDef<IMarketer>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => {
        const name = (getValue() as string) || "—";
        return <span className="font-medium text-gray-900">{name}</span>;
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone_number",
      header: "Phone Number",
      cell: ({ getValue }) => (getValue() as string) || "—",
    },
    {
      accessorKey: "referral_code",
      header: "Referral Code",
      cell: ({ getValue }) => (getValue() as string) || "—",
    },
    {
      accessorKey: "application_status",
      header: "Application Status",
      cell: ({ getValue }) => {
        const status = getValue() as IMarketer["application_status"];
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
              status === "approved"
                ? "bg-primary-100/20 text-primary-700"
                : "bg-amber-100/20 text-amber-700"
            }`}
          >
            {status || "pending"}
          </span>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Account",
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const { id, is_active: isActive } = row.original;
        const isUpdating = loadingMarketerId === id;
        return (
          <ItemAction
            show_view
            show_edit
            show_delete={true}
            show_toggle
            toggle_checked={isActive}
            toggle_loading={isUpdating}
            onView={() => setViewingMarketerId(id)}
            onEdit={() => setEditingMarketer(row.original)}
            onDelete={() => {
              if (
                window.confirm("Are you sure you want to delete this marketer?")
              ) {
                void delete_marketer(dispatch, id);
              }
            }}
            onToggle={(checked) =>
              toggle_marketer_status(dispatch, id, checked)
            }
          />
        );
      },
    },
  ];

  const lastPage = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      {loading && (
        <p className="mb-2 text-sm text-gray-500" role="status">
          Loading marketers...
        </p>
      )}
      <DataTable
        columns={columns}
        data={marketers}
        current_page={page}
        last_page={lastPage}
        onNext={() => page < lastPage && setPage(page + 1)}
        onPrev={() => page > 1 && setPage(page - 1)}
      />
      <ProfessionalViewModal
        open={viewingMarketerId !== null}
        type="marketer"
        id={viewingMarketerId}
        onClose={() => setViewingMarketerId(null)}
      />
      <MarketerFormModal
        open={createOpen || editingMarketer !== null}
        marketer={editingMarketer}
        onClose={() => {
          setEditingMarketer(null);
          onCreateClose();
        }}
      />
    </>
  );
};

export default MarketersTable;
